import { useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { HiOutlinePlus, HiOutlineTrash, HiOutlineDuplicate, HiOutlinePencil, HiOutlineChartBar } from "react-icons/hi";
import { deleteSurvey, duplicateSurvey } from "../../redux/actions/surveyActions";
import { DeleteDialog } from "../shared/DeleteDialog";
import { Snackbar } from "../shared/Snackbar";
import Button from "../shared/Button";
import Card from "../shared/Card";
import Badge from "../shared/Badge";

const SurveyGrid = ({ surveys, onCreateSurvey, onViewSurvey, onEditSurvey, isAdmin = true }) => {
    const dispatch = useDispatch();
    const currentOrganisation = useSelector((state) => state.organisation?.currentOrganisation);
    const [deletingSurvey, setDeletingSurvey] = useState(null);
    const [isDeleting, setIsDeleting] = useState(false);
    const [snackbar, setSnackbar] = useState({ show: false, message: "", type: "success" });

    const showSnackbar = (message, type = "success") => {
        setSnackbar({ show: true, message, type });
        setTimeout(() => setSnackbar({ show: false, message: "", type: "success" }), 3000);
    };

    const handleDelete = async () => {
        if (!deletingSurvey) return;
        setIsDeleting(true);
        try {
            const result = await dispatch(deleteSurvey(deletingSurvey._id, currentOrganisation._id));
            if (result.success) {
                showSnackbar("Survey deleted successfully");
            } else {
                showSnackbar(result.error || "Failed to delete survey", "error");
            }
            setDeletingSurvey(null);
        } finally {
            setIsDeleting(false);
        }
    };

    const handleDuplicate = async (survey) => {
        const result = await dispatch(duplicateSurvey(survey._id, currentOrganisation._id));
        if (result.success) {
            showSnackbar("Survey duplicated successfully");
        } else {
            showSnackbar(result.error || "Failed to duplicate survey", "error");
        }
    };

    const getStatusVariant = (status) => {
        switch (status) {
            case "active":
                return "success";
            case "paused":
                return "warning";
            case "ended":
                return "coral";
            case "draft":
            default:
                return "default";
        }
    };

    const formatDate = (date) => {
        if (!date) return "N/A";
        return new Date(date).toLocaleDateString("en-US", {
            year: "numeric",
            month: "short",
            day: "numeric",
        });
    };

    return (
        <div className="p-6 bg-canvas min-h-screen">
            {/* Header */}
            <div className="flex justify-between items-center mb-6 gap-4 flex-wrap">
                <div>
                    <h1 className="text-display-md text-ink" data-help-title="Surveys" data-help="Surveys let you collect feedback or information from your members. Design a survey here, share its public link, and see responses come in.">Surveys</h1>
                    <p className="text-body-md text-muted mt-1">
                        Create and manage surveys to collect feedback from your members
                    </p>
                </div>
                {isAdmin && (
                    <Button
                        variant="primary"
                        size="sm"
                        onClick={onCreateSurvey}
                        leadingIcon={<HiOutlinePlus className="text-xl" />}
                        data-help-title="Create survey"
                        data-help="Opens the survey builder where you set a title, optional description, questions, and behavior settings. Available to admins only."
                    >
                        Create Survey
                    </Button>
                )}
            </div>

            {/* Surveys Grid */}
            {surveys.length === 0 ? (
                <Card variant="surface-soft" className="text-center !p-16" data-help-title="No surveys yet" data-help="Shows when your organisation has no surveys yet. Create your first survey to start collecting feedback from your members.">
                    <div className="text-5xl mb-4 text-muted">⌧</div>
                    <h3 className="text-title-md text-ink mb-2">No surveys yet</h3>
                    <p className="text-body-md text-muted mb-6">
                        Create your first survey to start collecting feedback
                    </p>
                    {isAdmin && (
                        <Button variant="primary" size="md" onClick={onCreateSurvey} data-help-title="Create survey" data-help="Opens the survey builder so you can create your first survey. Available to admins only.">
                            Create Survey
                        </Button>
                    )}
                </Card>
            ) : (
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                    {surveys.map((survey) => (
                        <Card
                            key={survey._id}
                            variant="default"
                            className="cursor-pointer hover:shadow-card transition-shadow"
                            onClick={() => onViewSurvey(survey)}
                            data-help-title="Survey card"
                            data-help="Shows one survey with its status, title, and stats for visits, responses, and question count. Clicking the card opens the survey's detail view with its responses, configuration, and shareable link."
                        >
                            {/* Status Badge */}
                            <div className="flex justify-between items-start mb-4">
                                <Badge variant={getStatusVariant(survey.status)} data-help-title="Survey status" data-help="Shows the survey's lifecycle state: Draft (not yet published), Active (collecting responses), Paused, or Ended.">
                                    {survey.status.charAt(0).toUpperCase() + survey.status.slice(1)}
                                </Badge>
                            </div>

                            {/* Title & Description */}
                            <h3 className="text-title-sm text-ink mb-2 line-clamp-2">
                                {survey.title}
                            </h3>
                            {survey.description && (
                                <p className="text-body-md text-muted mb-4 line-clamp-2">
                                    {survey.description}
                                </p>
                            )}

                            {/* Stats */}
                            <div className="grid grid-cols-3 gap-2 mb-4 py-4 border-t border-b border-hairline">
                                <div className="text-center">
                                    <div className="text-title-md text-ink font-medium">
                                        {survey.analytics?.totalVisits || 0}
                                    </div>
                                    <div className="text-[12px] text-muted">Visits</div>
                                </div>
                                <div className="text-center">
                                    <div className="text-title-md text-ink font-medium">
                                        {survey.analytics?.totalCompleted || 0}
                                    </div>
                                    <div className="text-[12px] text-muted">Responses</div>
                                </div>
                                <div className="text-center">
                                    <div className="text-title-md text-ink font-medium">
                                        {survey.questions?.length || 0}
                                    </div>
                                    <div className="text-[12px] text-muted">Questions</div>
                                </div>
                            </div>

                            {/* Dates */}
                            <div className="text-[12px] text-muted mb-4">
                                <div>Created: {formatDate(survey.createdAt)}</div>
                                {survey.startDate && <div>Started: {formatDate(survey.startDate)}</div>}
                            </div>

                            {/* Actions */}
                            <div className="flex gap-2" onClick={(e) => e.stopPropagation()}>
                                <Button
                                    variant="secondary"
                                    size="sm"
                                    onClick={() => onViewSurvey(survey)}
                                    leadingIcon={<HiOutlineChartBar />}
                                    className="flex-1"
                                    data-help-title="Analytics"
                                    data-help="Opens this survey's detail view focused on response analytics, including visit counts, completion stats, and the responses collected so far."
                                >
                                    Analytics
                                </Button>
                                {isAdmin && (
                                    <>
                                        <button
                                            onClick={() => onEditSurvey(survey)}
                                            className="p-2 rounded-md text-muted hover:text-ink hover:bg-surface-soft transition-colors"
                                            title="Edit"
                                            data-help-title="Edit survey"
                                            data-help="Opens the survey builder pre-filled with this survey so you can change its questions, settings, and translations. Available to admins only."
                                        >
                                            <HiOutlinePencil className="text-lg" />
                                        </button>
                                        <button
                                            onClick={() => handleDuplicate(survey)}
                                            className="p-2 rounded-md text-muted hover:text-ink hover:bg-surface-soft transition-colors"
                                            title="Duplicate"
                                            data-help-title="Duplicate survey"
                                            data-help="Creates a copy of this survey in your organisation, useful for reusing an existing survey as a template. Available to admins only."
                                        >
                                            <HiOutlineDuplicate className="text-lg" />
                                        </button>
                                        <button
                                            onClick={() => setDeletingSurvey(survey)}
                                            className="p-2 rounded-md text-muted hover:text-signature-coral hover:bg-surface-soft transition-colors"
                                            title="Delete"
                                            data-help-title="Delete survey"
                                            data-help="Permanently deletes this survey after a confirmation dialog. This cannot be undone. Available to admins only."
                                        >
                                            <HiOutlineTrash className="text-lg" />
                                        </button>
                                    </>
                                )}
                            </div>
                        </Card>
                    ))}
                </div>
            )}

            {/* Delete Confirmation Dialog */}
            {deletingSurvey && (
                <DeleteDialog
                    yesFunction={handleDelete}
                    noFunction={() => setDeletingSurvey(null)}
                    isLoading={isDeleting}
                />
            )}

            {/* Snackbar */}
            {snackbar.show && (
                <Snackbar
                    text={snackbar.message}
                    successIcon={snackbar.type === "success"}
                    onClose={() => setSnackbar({ show: false, message: "", type: "success" })}
                />
            )}
        </div>
    );
};

export default SurveyGrid;
